import React from 'react';
import Localize from 'react-intl-universal';

import { format } from 'date-fns';
import { useFormikContext } from 'formik';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { formatDateTimeStringToDateTimeObject } from '@common/Util';
import DatePicker from '@components/DatePicker';
import DateTimePicker from '@components/DateTimePicker';
import Timepicker from '@components/Timepicker';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250
    }
  }
};

const AdditionalInformationTab = () => {
  const { values, handleChange, setFieldValue } = useFormikContext();
  const allergyOptions = values.allergies; // TODO: Get data from API

  const handleAllergyChange = (event) => {
    const selectedValues = event.target.value;

    // Get full allergy objects from the options by matching selected `value`
    const selectedObjects = allergyOptions.filter((opt) => selectedValues.includes(opt.value));
    setFieldValue('allergies', selectedObjects);
  };

  const handleCustomFieldChange = (index, newValue) => {
    const updatedCustomFields = [...values.customFields];
    const fieldTypeKey = updatedCustomFields[index]?.customFieldResponseDto?.customFieldType?.key;

    if (fieldTypeKey === 'drop') {
      updatedCustomFields[index].valueEntity = { name: newValue };
    } else {
      updatedCustomFields[index].jsonValue = newValue;
    }

    setFieldValue('customFields', updatedCustomFields);
  };

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <FormControl component='fieldset'>
          <FormLabel component='legend'>{Localize.get('Booking.hotelRequest')}</FormLabel>
          <RadioGroup
            row
            name='hotelRequest'
            value={values.hotelRequest}
            onChange={handleChange}
            disabled
          >
            <FormControlLabel
              value='yes'
              control={<Radio disabled />}
              label={Localize.get('App.yes')}
            />
            <FormControlLabel
              value='no'
              control={<Radio disabled />}
              label={Localize.get('App.no')}
            />
          </RadioGroup>
          {values.hotelRequest ? (
            <FormLabel component='legend'>
              {formatDateTimeStringToDateTimeObject(values.arrivalDate)}
              {values.departureDate ? '-' : ''}
              {formatDateTimeStringToDateTimeObject(values.departureDate)}
            </FormLabel>
          ) : (
            ''
          )}
        </FormControl>

        <FormControl component='fieldset'>
          <FormLabel component='legend'>{Localize.get('Booking.foodPreferences')}</FormLabel>
          <FormGroup row>
            <FormControlLabel
              control={
                <Checkbox
                  name='foodPreferences.vegan'
                  checked={values.foodPreferences.vegan}
                  onChange={handleChange}
                  disabled
                />
              }
              label={Localize.get('Booking.vegan')}
            />
            <FormControlLabel
              control={
                <Checkbox
                  name='foodPreferences.vegetarian'
                  checked={values.foodPreferences.vegetarian}
                  onChange={handleChange}
                  disabled
                />
              }
              label={Localize.get('Booking.vegetarian')}
            />
          </FormGroup>
        </FormControl>

        <FormControl component='fieldset'>
          <FormLabel component='legend'>{Localize.get('Booking.healthNeeds')}</FormLabel>
          <FormGroup row>
            <FormControlLabel
              control={
                <Checkbox
                  name='healthNeeds.handicapped'
                  checked={values.healthNeeds.handicapped}
                  onChange={handleChange}
                  disabled
                />
              }
              label={Localize.get('Booking.handicappedField')}
            />
          </FormGroup>
        </FormControl>

        <FormControl fullWidth>
          <InputLabel id='allergies-label'>{Localize.get('Booking.allergies')}</InputLabel>
          <Select
            labelId='allergies-label'
            id='allergies'
            multiple
            disabled
            value={values.allergies.map((a) => a.value)}
            onChange={handleAllergyChange}
            input={<OutlinedInput label={Localize.get('Booking.allergies')} />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((val) => (
                  <Chip key={val} label={val} />
                ))}
              </Box>
            )}
            MenuProps={MenuProps}
          >
            {allergyOptions.map((option) => (
              <MenuItem key={option.id} value={option.value}>
                <Checkbox checked={values.allergies.some((a) => a.value === option.value)} />
                {option.value}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {values.customFields?.length ? (
        <>
          <FormLabel component='legend' sx={{ fontWeight: 'bold', marginTop: '16px' }}>
            {Localize.get('Booking.additionalCustomFields')}
          </FormLabel>
          <Grid container spacing={2} mt={1}>
            {values.customFields
              ?.sort(
                (a, b) =>
                  a.customFieldResponseDto.fieldOrderNumber -
                  b.customFieldResponseDto.fieldOrderNumber
              )
              .map((fieldWrapper, index) => {
                const { customFieldResponseDto: field, jsonValue } = fieldWrapper;
                const fieldType = field.customFieldType?.key;
                const label =
                  field.label || field.name || `${Localize.get('Booking.field')} ${index + 1}`;
                const isMandatory = field.isMandatory;
                const predefinedValues = field.predefinedValues || [];

                let value = field.defaultValue?.value || '';

                switch (fieldType) {
                  case 'text':
                    value = typeof jsonValue === 'string' ? jsonValue : '';
                    return (
                      <Grid item xs={12} sm={6} key={field.id}>
                        <TextField
                          key={index}
                          label={label}
                          disabled
                          fullWidth
                          value={value}
                          onChange={(e) => handleCustomFieldChange(index, e.target.value)}
                          required={isMandatory}
                        />
                      </Grid>
                    );

                  case 'drop':
                    value = predefinedValues.find((v) => v.id === jsonValue)?.valueLabel || '';
                    return (
                      <Grid item xs={12} sm={6} key={field.id}>
                        <FormControl fullWidth key={index} required={isMandatory}>
                          <InputLabel>{label}</InputLabel>
                          <Select
                            value={value}
                            label={label}
                            disabled
                            onChange={(e) => handleCustomFieldChange(index, e.target.value)}
                          >
                            {predefinedValues
                              .sort((a, b) => a.valueOrderId - b.valueOrderId)
                              .map((option) => (
                                <MenuItem key={option.id} value={option.valueLabel}>
                                  {option.valueLabel}
                                </MenuItem>
                              ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    );

                  case 'date':
                    value = jsonValue ?? null;
                    return (
                      <Grid item xs={12} sm={6} key={field.id}>
                        <DatePicker
                          key={index}
                          label={label}
                          disabled
                          value={value}
                          onChange={(newDate) =>
                            handleCustomFieldChange(index, newDate?.toISOString())
                          }
                          slotProps={{ textField: { fullWidth: true, required: isMandatory } }}
                        />
                      </Grid>
                    );

                  case 'time':
                    value = jsonValue ?? null;
                    return (
                      <Grid item xs={12} sm={6} key={field.id}>
                        <Timepicker
                          label={label}
                          disabled
                          value={value}
                          onChange={(val) => setFieldValue(field.name, val)}
                          renderInput={(params) => <TextField {...params} />}
                          ampm={false}
                        />
                      </Grid>
                    );

                  case 'date_time':
                    value = jsonValue ?? null;
                    return (
                      <Grid item xs={12} sm={6} key={field.id}>
                        <DateTimePicker
                          key={index}
                          label={label}
                          disabled
                          value={value}
                          onChange={(newDate) => {
                            const formatted = newDate
                              ? format(newDate, 'yyyy-MM-dd HH:mm:ss')
                              : null;
                            handleCustomFieldChange(index, formatted);
                          }}
                          slotProps={{ textField: { fullWidth: true, required: isMandatory } }}
                          renderInput={(params) => <TextField {...params} />}
                          ampm={false}
                        />
                      </Grid>
                    );

                  case 'quant':
                    value = Number(jsonValue) || '';
                    return (
                      <Grid item xs={12} sm={6} key={field.id}>
                        <TextField
                          key={index}
                          label={label}
                          disabled
                          type='number'
                          fullWidth
                          value={value}
                          onChange={(e) => handleCustomFieldChange(index, e.target.value)}
                          required={isMandatory}
                          inputProps={{
                            min: field.fromValue || undefined,
                            max: field.untilValue || undefined
                          }}
                        />
                      </Grid>
                    );

                  case 'check':
                    value = jsonValue || null;
                    return (
                      <Grid item xs={12} sm={6} key={field.id}>
                        <FormControl component='fieldset' fullWidth required={isMandatory}>
                          <FormLabel component='legend'>{label}</FormLabel>
                          <FormGroup row>
                            {predefinedValues.map((option) => {
                              const isChecked =
                                typeof value === 'object' && value[option.id] === true;

                              return (
                                <FormControlLabel
                                  key={option.id}
                                  control={
                                    <Checkbox
                                      checked={isChecked}
                                      disabled
                                      onChange={() => {
                                        const updatedValue = {
                                          ...(value || {}),
                                          [option.id]: !(value?.[option.id] || false)
                                        };
                                        handleCustomFieldChange(index, updatedValue);
                                      }}
                                    />
                                  }
                                  label={option.valueLabel}
                                />
                              );
                            })}
                          </FormGroup>
                        </FormControl>
                      </Grid>
                    );

                  case 'radio':
                    value = predefinedValues.find((v) => v.id === jsonValue)?.valueLabel || '';
                    return (
                      <Grid item xs={12} sm={6} key={field.id}>
                        <FormControl component='fieldset' fullWidth required={isMandatory}>
                          <FormLabel component='legend'>{label}</FormLabel>
                          <RadioGroup
                            name={`customFields[${index}].jsonValue`}
                            value={value}
                            onChange={(e) => handleCustomFieldChange(index, e.target.value)}
                            row
                          >
                            {predefinedValues.map((option) => (
                              <FormControlLabel
                                key={option.id}
                                value={option.valueLabel}
                                control={<Radio disabled />}
                                label={option.valueLabel}
                              />
                            ))}
                          </RadioGroup>
                        </FormControl>
                      </Grid>
                    );

                  case 'area':
                    value = typeof jsonValue === 'string' ? jsonValue : '';
                    return (
                      <Grid item xs={12} sm={6} key={field.id}>
                        <TextField
                          key={index}
                          fullWidth
                          multiline
                          rows={4}
                          label={label}
                          value={value}
                          disabled
                          onChange={(e) => handleCustomFieldChange(index, e.target.value)}
                          required={isMandatory}
                        />
                      </Grid>
                    );

                  default:
                    return (
                      <Grid item xs={12} sm={6} key={field.id}>
                        <Typography key={index} color='error'>
                          {Localize.get('Booking.unsupportedFieldType')}: {fieldType}
                        </Typography>
                      </Grid>
                    );
                }
              })}
          </Grid>
        </>
      ) : (
        <></>
      )}
    </>
  );
};

export default AdditionalInformationTab;
